import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock } from "vue"

import { onMounted, onUnmounted, watch, ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import HeroBanner from './components/base/HeroBanner.vue';
import AppHeader from '@/components/base/AppHeader.vue'
import AppFooter from './components/base/AppFooter.vue';
import ToastComponent from '@/components/ToastComponent.vue'
import { useToast } from '@/composables/useToast'
import { useI18n } from 'vue-i18n'
import BetslipModal from '@/components/BetslipModal.vue'
import VirtualKeyboardHost from '@/components/VirtualKeyboardHost.vue'
import GlobalLoader from '@/components/GlobalLoader.vue'
import { useModalStore } from '@/stores/modal'
import { useAuthStore } from '@/stores/auth'
import { useGameStore } from '@/stores/game'
import { useBetslipStore } from '@/stores/betslip'
import { getThemeParamFromUrl, initializeTheme, setTheme } from '@/composables/useTheme'
import { resolveThemeName } from '@/utils/themeLoader'
import { usePromotionStore } from '@/stores/promotion'
import { normalizePromotionConfig } from '@/types/promotion'
import { iframeCommunication } from '@/utils/iframeCommunication'
import { persistIntegrationMode, hasIntegrationQueryInLocation } from '@/utils/integrationMode'
import { setApiBaseUrl } from '@/api'
import { useIntegrationMode } from '@/composables/useIntegrationMode'
import { usePromotionSync } from '@/composables/usePromotionSync'
import { useUpcomingDrawsStore } from '@/stores/upcomingDraws'


export default /*@__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props, { expose: __expose }) {

const { toast, showToast } = useToast()
const { t } = useI18n()
const modalStore = useModalStore()
const authStore = useAuthStore()
const gameStore = useGameStore()
const betslipStore = useBetslipStore()
const promotionStore = usePromotionStore()
const upcomingDrawsStore = useUpcomingDrawsStore()
const { refreshPromotions, applyPartnerPromotion } = usePromotionSync(showToast)
const route = useRoute()
const router = useRouter()
const iframeCleanupHandlers: Array<() => void> = []

const isInitialized = ref(false)
const viewportWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024)

const { isLite } = useIntegrationMode()
const isWidget = computed(() => route.name === 'widget-upcoming')

const showAppFooter = computed(() => {
  if (route.name !== 'lottery-detail') return true
  return viewportWidth.value > 768
})

const onViewportResize = () => {
  viewportWidth.value = window.innerWidth
}

const openBetslipModal = () => {
  modalStore.openBetslip()
}

const closeBetslipModal = () => {
  modalStore.closeBetslip()
}

const refreshAccountScopedCatalog = async () => {
  upcomingDrawsStore.invalidateAccountContext()
  await upcomingDrawsStore.fetchDraws(1, undefined, true)
  await upcomingDrawsStore.fetchFeaturedDraws(1, undefined, true)
  gameStore.clearProducts()
}

watch(
  () => authStore.account,
  async (newAccount, oldAccount) => {
    if (!newAccount || newAccount === oldAccount) {
      return
    }
    try {
      await refreshAccountScopedCatalog()
    } catch (error) {
      console.error('Failed to refresh draws after account change:', error)
    }
  },
  { immediate: false }
)

// Watch for account changes in URL
watch(
  () => route.query.account,
  async (newAccount, oldAccount) => {
    if (newAccount !== oldAccount && newAccount && typeof newAccount === 'string') {
      authStore.account = newAccount
      sessionStorage.setItem('auth_account', newAccount)
      betslipStore.syncStorageContext()
      
      try {
        await initializeTheme(true)
        const currency = sessionStorage.getItem('auth_currency')
        if (currency) {
          await gameStore.fetchGameSettings(false)
        }
        await refreshPromotions(undefined, { silent: true })
        await refreshAccountScopedCatalog()
      } catch (error) {
        console.error('Failed to re-initialize theme:', error)
      }
    }
  },
  { immediate: false }
)

watch(
  () => route.query.currency,
  async (newCurrency, oldCurrency) => {
    if (newCurrency !== oldCurrency && newCurrency && typeof newCurrency === 'string') {
      const cleanCurrency = newCurrency.split('?')[0]
      sessionStorage.setItem('auth_currency', cleanCurrency)
      betslipStore.syncStorageContext()

      try {
        if (authStore.account) {
          await gameStore.fetchGameSettings(false, true)
        }
        await refreshPromotions(undefined, { silent: true })
      } catch (error) {
        console.error('Failed to refresh game settings after currency change:', error)
      }
    }
  },
  { immediate: false }
)

// Only apply game-settings theme when URL has no theme param (URL wins)
watch(
  () => gameStore.getBetSettings?.theme,
  async (newTheme) => {
    if (newTheme && !getThemeParamFromUrl()) {
      try {
        await setTheme(resolveThemeName(newTheme))
      } catch (error) {
        console.error('Failed to update theme from game settings:', error)
      }
    }
  },
  { immediate: false }
)

const isInIframe = iframeCommunication.isRunningInIframe()

watch(isLite, (lite) => {
  if (typeof document === 'undefined') return
  document.body.classList.toggle('integration-lite', lite)
}, { immediate: true })

watch(isWidget, (widget) => {
  if (typeof document === 'undefined') return
  document.body.classList.toggle('integration-widget', widget)
}, { immediate: true })

watch(
  () => [authStore.token, gameStore.getBetSettings?.enabled_modules, gameStore.getBetSettings?.promotion_source],
  async () => {
    if (!isInitialized.value) return
    if (!gameStore.getBetSettings) return
    await refreshPromotions(undefined, { silent: true })
  }
)

onMounted(async () => {
  window.addEventListener('resize', onViewportResize)
  const handleParentLogout = () => {
    authStore.logout({ silent: true, reason: 'host-request' })
    modalStore.closeBetslip()
    showToast({
      title: t('auth.loggedOut'),
      message: t('auth.sessionClosed'),
      type: 'info',
      duration: 4000
    })
    router.replace({ path: '/next-draws', query: {} })
  }

  const handleParentNavigate = (data?: Record<string, unknown>) => {
    try {
      if (!data) return
      const target = data.route || data.path || data.name
      if (!target || typeof target !== 'string') return
      
      const query = (data.query && typeof data.query === 'object') 
        ? data.query as Record<string, string> 
        : undefined

      if (target.startsWith('/')) {
        router.push({ path: target, query })
      } else {
        router.push({ name: target, query })
      }
    } catch (e) {
      console.error('Iframe navigation failed:', e)
    }
  }

  // CORE HANDSHAKE HANDLER: This is where configuration is synchronized
  const handleParentReady = async (data?: Record<string, unknown>) => {
    console.log('[App] Handshake received from parent:', data)
    if (data?.config && typeof data.config === 'object') {
      const config = data.config as Record<string, any>

      authStore.applyParentConfig(config)
      betslipStore.syncStorageContext()

      if (config.GAME_SERVER_URL) {
        setApiBaseUrl(config.GAME_SERVER_URL)
      }

      try {
        await initializeTheme(true)
        const currency = config.currency || sessionStorage.getItem('auth_currency')
        if (currency) {
          await gameStore.fetchGameSettings(false)
        }
        await refreshPromotions(config, { silent: true })
      } catch (e) {
        console.warn('Initialization after handshake failed:', e)
      }
    } else {
      await initializeTheme()
    }

    isInitialized.value = true
  }

  const handleClearBetslip = () => {
    betslipStore.clearBetslip()
    showToast({
      title: t('toast.betslipCleared'),
      message: t('toast.betslipClearedMsg'),
      type: 'info',
      duration: 3000
    })
  }

  const handlePromotionAvailable = (data?: Record<string, unknown>) => {
    if (!data) return
    applyPartnerPromotion({ promotion: data })
  }

  const handlePromotionCleared = () => {
    promotionStore.clear('partner_cleared')
  }

  const handlePlacePromotion = (data?: Record<string, unknown>) => {
    if (!data) return
    const currency =
      (typeof data.currency === 'string' && data.currency) ||
      sessionStorage.getItem('auth_currency') ||
      ''
    const stake = Number(data.stake ?? promotionStore.activePromotion?.stake)
    const normalized = normalizePromotionConfig({
      ...data,
      stake,
      currency,
    })
    if (!normalized) return
    promotionStore.apply(normalized)
    promotionStore.setApplyToSlip(true)
    window.dispatchEvent(new CustomEvent('bitville:place-promotion'))
  }

  // Register communication listeners
  iframeCleanupHandlers.push(iframeCommunication.on('logout', handleParentLogout))
  iframeCleanupHandlers.push(iframeCommunication.on('navigate', handleParentNavigate))
  iframeCleanupHandlers.push(iframeCommunication.on('parent-ready', handleParentReady))
  iframeCleanupHandlers.push(iframeCommunication.on('clear-betslip', handleClearBetslip))
  iframeCleanupHandlers.push(iframeCommunication.on('promotion-available', handlePromotionAvailable))
  iframeCleanupHandlers.push(iframeCommunication.on('promotion-cleared', handlePromotionCleared))
  iframeCleanupHandlers.push(iframeCommunication.on('place-promotion', handlePlacePromotion))

  if (isInIframe) {
    if (!hasIntegrationQueryInLocation()) {
      persistIntegrationMode(null)
    }
    document.body.classList.add('iframe-mode')
    document.documentElement.classList.add('iframe-mode')
    
    // Measure and report height
    iframeCommunication.startObserving('iframe-root')
    
    // START HANDSHAKE: Tell parent child is ready to receive configuration
    iframeCommunication.sendReady()

    // Safety timeout: If handshake doesn't complete in 4s, proceed with default init
    setTimeout(async () => {
      if (!isInitialized.value) {
        console.warn('[App] Handshake timeout - proceeding with default initialization')
        await initializeTheme()
        await refreshAccountScopedCatalog()
        isInitialized.value = true
      }
    }, 4000)

    // Handle deep links from URL params
    const gameSlug = (route.query.game || route.query.gameSlug) as string | undefined
    if (gameSlug && typeof gameSlug === 'string' && gameSlug.trim()) {
      const slug = gameSlug.trim()
      const restQuery = { ...route.query }
      delete restQuery.game
      delete restQuery.gameSlug
      router.replace({ path: `/lottery/${slug}`, query: restQuery as Record<string, string> })
    }
  } else {
    // Standard standalone mode
    const account = authStore.account || sessionStorage.getItem('auth_account') || (route.query.account as string)
    if (account) {
      authStore.account = account
      sessionStorage.setItem('auth_account', account)
      betslipStore.syncStorageContext()
    }

    const token = (route.query.token as string)
    if (token) {
      authStore.token = token
      sessionStorage.setItem('auth_token', token)
    }

    const currencyQuery = route.query.currency as string | undefined
    if (currencyQuery && typeof currencyQuery === 'string' && currencyQuery.trim()) {
      const cleanCurrency = currencyQuery.split('?')[0]
      sessionStorage.setItem('auth_currency', cleanCurrency)
      betslipStore.syncStorageContext()
    }

    try {
      await initializeTheme()
      const currency = sessionStorage.getItem('auth_currency')
      if (account && currency) {
        await gameStore.fetchGameSettings(false)
        authStore.refreshAuthFromExternalSources()
        await refreshPromotions(undefined, { silent: true })
      }
    } catch (e) {
      console.warn('Failed to load initial settings:', e)
    }

    isInitialized.value = true
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', onViewportResize)
  iframeCommunication.destroy()
  iframeCleanupHandlers.forEach((cleanup) => cleanup())
})

__expose({
  openBetslipModal,
  closeBetslipModal
})

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock("div", {
    id: "iframe-root",
    class: _normalizeClass({ 'is-iframe': _unref(isInIframe) })
  }, [
    (!_unref(isLite) && !isWidget.value)
      ? (_openBlock(), _createBlock(HeroBanner, { key: 0 }))
      : _createCommentVNode("", true),
    (!isWidget.value)
      ? (_openBlock(), _createBlock(AppHeader, { key: 1 }))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: _normalizeClass(["app-content", { 'app-content--lite': _unref(isLite) }])
    }, [
      _createVNode(_component_router_view, { onOpenBetslipModal: openBetslipModal }),
      (showAppFooter.value && !_unref(isLite) && !isWidget.value)
        ? (_openBlock(), _createBlock(AppFooter, { key: 0 }))
        : _createCommentVNode("", true),
      _createVNode(VirtualKeyboardHost),
      _createVNode(GlobalLoader),
      (_unref(toast))
        ? (_openBlock(), _createBlock(ToastComponent, {
            key: 1,
            centered: _unref(toast).centered,
            title: _unref(toast).title,
            message: _unref(toast).message,
            type: _unref(toast).type,
            duration: _unref(toast).duration
          }, null, 8, ["centered", "title", "message", "type", "duration"]))
        : _createCommentVNode("", true),
      _createVNode(BetslipModal, {
        "is-open": _unref(modalStore).showBetslip,
        onClose: closeBetslipModal
      }, null, 8, ["is-open"])
    ], 2)
  ], 2))
}
}

})